import React, { useState, useMemo, useEffect } from "react";

interface Column {
  key: string;
  label: string;
  sortable?: boolean;
  render?: (row: any) => React.ReactNode;
}

interface Props {
  title?: string;
  columns: Column[];
  data: any[];
  onRowClick?: (row: any) => void;
  selectable?: boolean;
  pagination?: boolean;
  pageSize?: number;
  checkbox?: boolean;
  onSelectionChange?: (rows: any[]) => void;
}

const ClsGrid: React.FC<Props> = ({
  title,
  columns,
  data,
  onRowClick,
  selectable = false,
  pagination = true,
  pageSize = 10,
  checkbox = false,
  onSelectionChange,
}) => {
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortAsc, setSortAsc] = useState(true);
  const [selectedRow, setSelectedRow] = useState<number | null>(null);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");

  const filteredData = useMemo(() => {
    if (!search) return data;

    return data.filter((row) =>
      Object.values(row).some((val) =>
        String(val).toLowerCase().includes(search.toLowerCase())
      )
    );
  }, [search, data]);

  const sortedData = useMemo(() => {
    if (!sortKey) return filteredData;

    return [...filteredData].sort((a, b) => {
      const valA = a[sortKey] || "";
      const valB = b[sortKey] || "";

      if (valA < valB) return sortAsc ? -1 : 1;
      if (valA > valB) return sortAsc ? 1 : -1;
      return 0;
    });
  }, [filteredData, sortKey, sortAsc]);

  const totalPages = Math.ceil(sortedData.length / pageSize);
  const paginatedData = pagination
    ? sortedData.slice((page - 1) * pageSize, page * pageSize)
    : sortedData;

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortAsc(!sortAsc);
    } else {
      setSortKey(key);
      setSortAsc(true);
    }
  };

  const toggleRow = (index: number) => {
    let updated: number[] = [];

    if (selectedRows.includes(index)) {
      updated = selectedRows.filter((i) => i !== index);
    } else {
      updated = [...selectedRows, index];
    }

    setSelectedRows(updated);
  };

  const toggleSelectAll = () => {
    if (selectedRows.length === paginatedData.length) {
      setSelectedRows([]);
    } else {
      const allIndexes = paginatedData.map((_, i) => i);
      setSelectedRows(allIndexes);
    }
  };

  useEffect(() => {
    if (onSelectionChange) {
      const selectedData = selectedRows.map((i) => paginatedData[i]);
      onSelectionChange(selectedData);
    }
  }, [selectedRows, paginatedData]);

  return (
    <div>
      {title && <h3 style={{ textAlign: "center" }}>{title}</h3>}

      <div style={{ marginBottom: "10px" }}>
        <input
          type="text"
          placeholder="Search..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          style={{ padding: "6px", width: "200px" }}
        />
      </div>

      <div style={{ maxHeight: "500px", overflow: "auto", border: "1px solid #80a0ff" }}>
        <table width="100%" style={{ borderCollapse: "collapse" }}>
          <thead style={{ position: "sticky", top: 0, background: "#CFE2FF", color: "#2859ac", fontWeight: "normal" }}>
            <tr style={{ fontWeight: "normal" }}>
              {checkbox && (
                <th style={{ border: "1px solid #80a0ff", padding: "8px" }}>
                  <input
                    type="checkbox"
                    checked={
                      selectedRows.length === paginatedData.length &&
                      paginatedData.length > 0
                    }
                    onChange={toggleSelectAll}
                  />
                </th>
              )}

              {columns.map((col) => (
                <th
                  key={col.key}
                  onClick={() => col.sortable && handleSort(col.key)}
                  style={{
                    border: "1px solid #80a0ff",
                    padding: "8px",
                    cursor: col.sortable ? "pointer" : "default",
                  }}
                >
                  {col.label}
                  {sortKey === col.key && (sortAsc ? " 🔼" : " 🔽")}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {paginatedData.map((row, index) => {
              const actualIndex = (page - 1) * pageSize + index;

              return (
                <tr
                  key={actualIndex}
                  onClick={() => {
                    if (selectable) setSelectedRow(actualIndex);
                    onRowClick?.(row);
                  }}
                  style={{
                    backgroundColor:
                      selectedRow === actualIndex
                        ? "orange"
                        : actualIndex % 2 === 0
                          ? "#f5faff"
                          : "#FFFFFF",
                    cursor: onRowClick ? "pointer" : "default",
                  }}
                >

                  {checkbox && (
                    <td style={{ textAlign: "center" }}>
                      <input
                        type="checkbox"
                        checked={selectedRows.includes(index)}
                        onChange={(e) => {
                          e.stopPropagation();
                          toggleRow(index);
                        }}
                      />
                    </td>
                  )}

                  {columns.map((col) => (
                    <td key={col.key} style={{ borderRight: "1px solid #80a0ff", padding: "6px" }}>
                      {col.render ? col.render(row) : row[col.key]}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* 🔹 Pagination */}
      {pagination && totalPages > 1 && (
        <div style={{ textAlign: "center", marginTop: "10px" }}>
          <button disabled={page === 1} onClick={() => setPage(page - 1)}>
            Prev
          </button>

          <span style={{ margin: "0 10px" }}>
            Page {page} / {totalPages}
          </span>

          <button disabled={page === totalPages} onClick={() => setPage(page + 1)}>
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default ClsGrid;